import { useMemo } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { useGetHarnessInvoicesQuery } from "@/api/providers/invoices"
import {
  useGetHarnessCustomerPaymentMethodsQuery,
  useGetHarnessPaymentTokenizationDetailQuery,
} from "@/api/providers/payment-methods"
import { useGetHarnessSubscriptionsQuery } from "@/api/providers/subscriptions"
import { AlertCircleIcon } from "@/common/elements/icons"
import { Button } from "@/common/elements/button"
import { Link } from "@/common/elements/link"
import { Skeleton } from "@/common/elements/skeleton"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/common/elements/tooltip"
import { useSelectedCustomerId } from "@/common/hooks/use-selected-customer-id"
import { ApiSession } from "@/common/auth/utils"
import { AuthenticatedUser } from "@/common/keycloak/keycloak"
import {
  UiPaymentMethodType,
  UiInvoiceStatus,
  formatSubscriptionBillingInterval,
  getInvoiceSummaryDisplay,
  getSubscriptionStatusLabel,
  mapHarnessInvoiceToUi,
  mapHarnessPaymentMethodToUi,
  mapHarnessSubscriptionToUi,
} from "@/plugins/controls/common/billing-mappers"
import {
  isCreditCard,
  normalizeCardCategoryKnown,
  parsePositiveSurchargePercent,
} from "@/plugins/controls/common/billing-payment-helpers"
import { formatCurrency, getTotalAmountCents } from "@/plugins/stub/billing-data/utils"
import { formatSubscriptionBusinessDate } from "@/plugins/controls/common/subscription-business-date"
import { getAutopaymentDate } from "./utils"

function formatDateOrFallback(dateText: string | null) {
  return formatSubscriptionBusinessDate(dateText)
}

export function SubscriptionsControl(_props: {
  authUser: AuthenticatedUser
  apiSession: ApiSession | null
  onLogout: () => Promise<void>
}) {
  const selectedCustomerId = useSelectedCustomerId()
  const subscriptionsQueryArg = selectedCustomerId ? { customerId: selectedCustomerId } : skipToken
  const invoicesQueryArg = selectedCustomerId ? { customerId: selectedCustomerId } : skipToken
  const paymentMethodsQueryArg = selectedCustomerId ? { customerId: selectedCustomerId } : skipToken

  const {
    data: subscriptionData = [],
    isLoading: isSubscriptionsLoading,
    isFetching: isSubscriptionsFetching,
  } = useGetHarnessSubscriptionsQuery(subscriptionsQueryArg)
  const {
    data: invoiceData = [],
    isLoading: isInvoicesLoading,
    isFetching: isInvoicesFetching,
  } = useGetHarnessInvoicesQuery(invoicesQueryArg)
  const {
    data: paymentMethodData = [],
    isLoading: isPaymentMethodsLoading,
    isFetching: isPaymentMethodsFetching,
  } = useGetHarnessCustomerPaymentMethodsQuery(paymentMethodsQueryArg)
  const {
    data: tokenizationDetail,
    isLoading: isTokenizationLoading,
    isFetching: isTokenizationFetching,
  } = useGetHarnessPaymentTokenizationDetailQuery()

  const isTableLoading = isSubscriptionsLoading || isSubscriptionsFetching || isInvoicesLoading || isInvoicesFetching
  const shouldShowLoading = selectedCustomerId !== null && isTableLoading

  const subscriptions = useMemo(
    () => subscriptionData.map((subscription, index) => mapHarnessSubscriptionToUi(subscription, index)),
    [subscriptionData]
  )
  const invoices = useMemo(() => invoiceData.map((invoice, index) => mapHarnessInvoiceToUi(invoice, index)), [invoiceData])
  const savedPaymentMethods = useMemo(
    () => paymentMethodData.map((method) => mapHarnessPaymentMethodToUi(method)).filter((method) => !method.isDisabled),
    [paymentMethodData]
  )
  const defaultSavedMethod = useMemo(
    () => savedPaymentMethods.find((method) => method.isDefault) ?? null,
    [savedPaymentMethods]
  )
  const surchargePercent = useMemo(() => {
    const candidate = tokenizationDetail?.creditCardSurchargePercent ?? null
    return parsePositiveSurchargePercent(candidate)
  }, [tokenizationDetail])
  const hasPaymentContext =
    selectedCustomerId !== null &&
    !isPaymentMethodsLoading &&
    !isPaymentMethodsFetching &&
    !isTokenizationLoading &&
    !isTokenizationFetching

  function getSurchargeTooltipCopy(percent: number | null) {
    if (percent === null) {
      return "This autopayment may have a credit card surcharge added."
    }

    return `This autopayment will have a ${percent}% credit card surcharge added.`
  }

  const outstandingInvoices = useMemo(
    () =>
      invoices.filter(
        (invoice) => invoice.status === UiInvoiceStatus.Due || invoice.status === UiInvoiceStatus.Overdue
      ),
    [invoices]
  )

  const monthlyRecurringAmount = getTotalAmountCents(
    subscriptions.map((subscription) => ({ amountCents: subscription.monthlyAmountCents }))
  )
  const outstandingAmount = getTotalAmountCents(outstandingInvoices)

  return (
    <section className="sm:rounded-lg sm:border sm:bg-card sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Subscriptions</h2>
          <p className="mt-1 text-sm text-muted-foreground">Manage subscriptions and jump directly into their invoices.</p>
        </div>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-3">
        <div className="rounded-md border bg-muted/30 p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Subscriptions</p>
          {shouldShowLoading ? <Skeleton className="mt-2 h-8 w-16" /> : <p className="mt-2 text-2xl font-semibold">{subscriptions.length}</p>}
        </div>
        <div className="rounded-md border bg-muted/30 p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Next Billing Amount</p>
          {shouldShowLoading ? <Skeleton className="mt-2 h-8 w-36" /> : <p className="mt-2 text-2xl font-semibold">{formatCurrency(monthlyRecurringAmount)}</p>}
        </div>
        <div className="rounded-md border bg-muted/30 p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Outstanding invoices</p>
          {shouldShowLoading ? <Skeleton className="mt-2 h-8 w-36" /> : <p className="mt-2 text-2xl font-semibold">{formatCurrency(outstandingAmount)}</p>}
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Subscription</th>
              <th className="px-4 py-3 font-medium">Start date</th>
              <th className="px-4 py-3 font-medium">End date</th>
              <th className="px-4 py-3 font-medium">Next invoice date</th>
              <th className="px-4 py-3 font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Invoices</th>
            </tr>
          </thead>
          <tbody>
            {selectedCustomerId === null ? (
              <tr className="border-t">
                <td className="px-4 py-8 text-center text-sm text-muted-foreground" colSpan={7}>
                  Select a customer to view subscriptions.
                </td>
              </tr>
            ) : null}

            {shouldShowLoading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`subscriptions-skeleton-${index}`} className="border-t">
                    <td className="px-4 py-3"><Skeleton className="h-5 w-40" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-24" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-24" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-28" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-28" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-6 w-24 rounded-full" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-8 w-28" /></td>
                  </tr>
                ))
              : null}

            {selectedCustomerId !== null && !isTableLoading && subscriptions.length === 0 ? (
              <tr className="border-t">
                <td className="px-4 py-8 text-center text-sm text-muted-foreground" colSpan={7}>
                  No subscriptions found for this customer.
                </td>
              </tr>
            ) : null}

            {selectedCustomerId !== null && !isTableLoading
              ? subscriptions.flatMap((subscription) => {
                  const subscriptionInvoices = invoices.filter((invoice) => invoice.subscriptionId === subscription.id)
                  const autopaymentDate = getAutopaymentDate(subscription, subscriptionInvoices)
                  const invoiceSummary = getInvoiceSummaryDisplay(subscriptionInvoices)
                  const productRows = subscription.products.map((product) => (
                    <tr key={`${subscription.id}-product-${product.id}`} className="border-t bg-muted/20">
                      <td className="px-4 py-2 text-xs text-muted-foreground">{product.name}</td>
                      <td className="px-4 py-2" />
                      <td className="px-4 py-2" />
                      <td className="px-4 py-2" />
                      <td className="px-4 py-2 whitespace-nowrap text-xs">
                        {formatCurrency(product.amountCents)}
                        {product.recurringBillingFrequency
                          ? ` / ${formatSubscriptionBillingInterval(product.recurringBillingFrequency)}`
                          : null}
                      </td>
                      <td className="px-4 py-2" />
                      <td className="px-4 py-2" />
                    </tr>
                  ))

                  return [
                    <tr key={subscription.id} className="border-t">
                      <td className="px-4 py-3">
                        <p className="font-medium">{subscription.name}</p>
                        <p className="text-xs text-muted-foreground">ID: {subscription.id}</p>
                        <p className="text-xs text-muted-foreground">State: {getSubscriptionStatusLabel(subscription.status)}</p>
                      </td>
                      <td className="px-4 py-3">{formatDateOrFallback(subscription.startDate)}</td>
                      <td className="px-4 py-3">{formatDateOrFallback(subscription.endDate)}</td>
                      <td className="px-4 py-3">
                        <p>{formatDateOrFallback(subscription.nextInvoiceDate)}</p>
                        {hasPaymentContext && autopaymentDate && defaultSavedMethod ? (
                          <div className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
                            <span>
                              You have a scheduled autopayment for this subscription on {formatSubscriptionBusinessDate(autopaymentDate)}
                            </span>
                            {defaultSavedMethod.type === UiPaymentMethodType.Card &&
                            isCreditCard(normalizeCardCategoryKnown(defaultSavedMethod.cardCategory)) &&
                            surchargePercent !== null ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    aria-label="Autopayment includes a credit card surcharge"
                                    className="inline-flex shrink-0 items-center rounded-sm text-amber-700 transition-colors hover:text-amber-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    type="button"
                                  >
                                    <AlertCircleIcon className="size-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" sideOffset={8}>
                                  {getSurchargeTooltipCopy(surchargePercent)}
                                </TooltipContent>
                              </Tooltip>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatCurrency(subscription.monthlyAmountCents)} / {formatSubscriptionBillingInterval(subscription.recurrenceInterval)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${invoiceSummary.className}`}>
                          {invoiceSummary.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/invoices?subscription=${subscription.id}`}>View invoices</Link>
                        </Button>
                      </td>
                    </tr>,
                    ...productRows,
                  ]
                })
              : null}
          </tbody>
        </table>
      </div>
    </section>
  )
}
